import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuickAIModal = ({ isOpen, onClose, onConfirm, subscriptionTier, user, loading = false }) => {
  const [url, setUrl] = useState('');
  const [downPaymentPercent, setDownPaymentPercent] = useState('20');
  const [interestRate, setInterestRate] = useState('5.5');
  const [amortizationPeriod, setAmortizationPeriod] = useState('25');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  const [closingCosts, setClosingCosts] = useState('0');
  const [schlPremium, setSchlPremium] = useState('');
  const [error, setError] = useState('');

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setUrl('');
      setDownPaymentPercent('20');
      setInterestRate('5.5');
      setAmortizationPeriod('25');
      setPaymentFrequency('monthly');
      setClosingCosts('0');
      setSchlPremium('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      setError('Veuillez entrer une URL de propriété');
      return;
    }

    if (!url.includes('http://') && !url.includes('https://')) {
      setError('Veuillez entrer une URL valide (commençant par http:// ou https://)');
      return;
    }

    setError('');

    try {
      await onConfirm({
        url: url.trim(),
        downPaymentPercent: parseFloat(downPaymentPercent) || 20,
        interestRate: parseFloat(interestRate) || 5.5,
        amortizationPeriod: parseInt(amortizationPeriod) || 25,
        paymentFrequency: paymentFrequency || 'monthly',
        closingCosts: parseFloat(closingCosts) || 0,
        schlPremium: schlPremium ? parseFloat(schlPremium) : null
      });
    } catch (err) {
      setError(err?.message || 'Une erreur est survenue');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 'var(--spacing-md)'
    }}
    onClick={onClose}
    >
      <div style={{
        background: 'var(--color-card)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-xl)',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--spacing-lg)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-sm)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)/10',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon name="Sparkles" size={20} color="var(--color-primary)" />
            </div>
            <h2 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              margin: 0
            }}>
              Ajout rapide par IA
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-muted-foreground)'
            }}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.875rem',
          color: 'var(--color-muted-foreground)',
          marginBottom: 'var(--spacing-lg)'
        }}>
          Entrez l'URL de la propriété et les paramètres de financement. L'IA remplira automatiquement toutes les informations et générera l'analyse.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-heading)',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              URL de la propriété *
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.centris.ca/..."
              required
              style={{
                width: '100%',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                color: 'var(--color-foreground)',
                background: 'var(--color-background)'
              }}
            />
            <p style={{
              fontFamily: 'var(--font-caption)',
              fontSize: '0.75rem',
              color: 'var(--color-muted-foreground)',
              marginTop: '4px',
              margin: 0
            }}>
              Lien vers la fiche de l'immeuble (Centris, DuProprio, etc.)
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-heading)',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Mise de fonds (%)
              </label>
              <input
                type="number"
                value={downPaymentPercent}
                onChange={(e) => setDownPaymentPercent(e.target.value)}
                min="0"
                max="100"
                step="0.1"
                required
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--color-foreground)',
                  background: 'var(--color-background)'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-heading)',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Taux d'intérêt (%)
              </label>
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                min="0"
                max="30"
                step="0.1"
                required
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--color-foreground)',
                  background: 'var(--color-background)'
                }}
              />
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-lg)'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-heading)',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Amortissement (ans)
              </label>
              <input
                type="number"
                value={amortizationPeriod}
                onChange={(e) => setAmortizationPeriod(e.target.value)}
                min="1"
                max="50"
                required
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--color-foreground)',
                  background: 'var(--color-background)'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-heading)',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Fréquence de paiement
              </label>
              <select
                value={paymentFrequency}
                onChange={(e) => setPaymentFrequency(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--color-foreground)',
                  background: 'var(--color-background)'
                }}
              >
                <option value="weekly">Hebdomadaire</option>
                <option value="biweekly">Bi-hebdomadaire</option>
                <option value="monthly">Mensuel</option>
                <option value="annual">Annuel</option>
              </select>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-lg)'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-heading)',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Frais de clôture ($)
              </label>
              <input
                type="number"
                value={closingCosts}
                onChange={(e) => setClosingCosts(e.target.value)}
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--color-foreground)',
                  background: 'var(--color-background)'
                }}
              />
              <p style={{
                fontFamily: 'var(--font-caption)',
                fontSize: '0.75rem',
                color: 'var(--color-muted-foreground)',
                marginTop: '4px',
                margin: 0
              }}>
                Frais de notaire, inspection, etc.
              </p>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-heading)',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                Prime SCHL (%)
              </label>
              <input
                type="number"
                value={schlPremium}
                onChange={(e) => setSchlPremium(e.target.value)}
                min="0"
                max="100"
                step="0.1"
                placeholder="Optionnel"
                style={{
                  width: '100%',
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--color-foreground)',
                  background: 'var(--color-background)'
                }}
              />
              <p style={{
                fontFamily: 'var(--font-caption)',
                fontSize: '0.75rem',
                color: 'var(--color-muted-foreground)',
                marginTop: '4px',
                margin: 0
              }}>
                % du montant du prêt (après mise de fonds)
              </p>
            </div>
          </div>

          {error && (
            <div style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-destructive)/10',
              border: '1px solid var(--color-destructive)/30',
              color: 'var(--color-destructive)',
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              marginBottom: 'var(--spacing-md)'
            }}>
              {error}
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: 'var(--spacing-sm)',
            justifyContent: 'flex-end'
          }}>
            <Button
              variant="outline"
              size="md"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              size="md"
              iconName={loading ? "Loader2" : "Sparkles"}
              iconPosition="left"
              type="submit"
              disabled={loading}
            >
              {loading ? 'Traitement en cours...' : 'Créer et analyser'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickAIModal;
